import type { StreamingCommand } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

class ToolCall {
	initialized: boolean = false;
	name: string = '';
	nameKeyIdx: number | null = null;
	arguments: {
		localId: string | null;
		trueValue: string;
		value: string;
		valueStartIdx: number | null;
		wrapperAttrs: Record<string, unknown> | null;
		wrapperType: string | null;
		attrs: Record<string, unknown> | null;
	} = {
		localId: null,
		trueValue: '',
		value: '',
		valueStartIdx: null,
		wrapperAttrs: null,
		wrapperType: null,
		attrs: null,
	};
}

// KEYS
const NAME = '"name"';
const LOCAL_ID = '"localId"';
const VALUE = '"value"';
const WRAPPER_ATTRS = '"wrapperAttrs"';
const WRAPPER_TYPE = '"wrapperType"';
const ATTRIBUTES_TYPE = '"attrs"';

// ACTIONS
const REPLACE = 'replaceNode';
const INSERT_BEFORE = 'insertNodeBefore';
const INSERT_AFTER = 'insertNodeAfter';
const APPEND = 'appendNode';
const APPEND_TO_END = 'appendNodeToEnd';
const REMOVE = 'removeNode';
const WRAP = 'wrap';
const WRAP_NODE = 'wrapNode';
const SET_ATTRIBUTES = 'setAttrs';

type StreamActionType = StreamingCommand['type'];

const actionsRequiringValue = new Set([
	REPLACE,
	INSERT_BEFORE,
	INSERT_AFTER,
	APPEND,
	APPEND_TO_END,
]);
function isStreamable(actionName: string): boolean {
	return actionsRequiringValue.has(actionName);
}

const actionsRequiringLocalId = new Set([
	REPLACE,
	INSERT_BEFORE,
	INSERT_AFTER,
	REMOVE,
	WRAP,
	WRAP_NODE,
	SET_ATTRIBUTES,
]);
function requiresLocalId(actionName: string): boolean {
	return actionsRequiringLocalId.has(actionName);
}

function isWrapAction(actionName: string): boolean {
	return actionName === WRAP || actionName === WRAP_NODE;
}

function isUpdateAction(actionName: string): boolean {
	return actionName === SET_ATTRIBUTES;
}

export class InitAction {
	readonly name: StreamActionType;
	readonly localId: string | null;
	readonly wrapperType: string | null;
	readonly wrapperAttrs: Record<string, unknown> | null;
	readonly attrs: Record<string, unknown> | null;

	constructor(
		name: StreamActionType,
		localId: string | null,
		wrapperType: string | null,
		wrapperAttrs: Record<string, unknown> | null,
		attrs: Record<string, unknown> | null,
	) {
		this.name = name;
		this.localId = localId;
		this.wrapperType = wrapperType;
		this.wrapperAttrs = wrapperAttrs;
		this.attrs = attrs;
	}
}

export class StreamAction {
	readonly partialValue: string;
	readonly rawPartialValue: string;

	constructor(rawPartialValue: string) {
		this.rawPartialValue = rawPartialValue;
		// eslint-disable-next-line require-unicode-regexp
		this.partialValue = rawPartialValue.replace(/\\/g, '');
	}
}

export class FinishAction {
	readonly isFinished: true;

	constructor() {
		this.isFinished = true;
	}
}

export type ParseResult = InitAction | StreamAction | FinishAction;

function findValue(input: string, key: string): { keyIndex: number; value: string } | null {
	const keyIndex = input.indexOf(key);

	if (keyIndex === -1) {
		return null;
	} else {
		let i = keyIndex + key.length;
		let value = '';

		while (i < input.length) {
			const char = input[i];
			const prevChar = i > 0 ? input[i - 1] : undefined;

			if ((char === ':' || char === ' ' || char === '"') && value.length === 0) {
				i++;
			} else if (char === '"' && prevChar !== '\\') {
				return { value, keyIndex };
			} else {
				value += char;
				i++;
			}
		}
	}

	return null;
}

function findAdfStartIdx(searchSpace: string): number | null {
	const key = VALUE;
	const keyIndex = searchSpace.indexOf(VALUE);

	if (keyIndex === -1) {
		return null;
	} else {
		let i = keyIndex + key.length;

		while (i < searchSpace.length) {
			const char = searchSpace[i];
			if (char === '"') {
				return i + 1;
			}
			i++;
		}
	}

	return null;
}

function findAdfEndIdx(
	searchSpace: string,
	startIdx: number,
): {
	endIndex: number | null;
	partial: string;
} {
	let partial = '';

	for (let i = startIdx; i < searchSpace.length; i++) {
		const char = searchSpace[i];
		const prevChar = i > 0 ? searchSpace[i - 1] : undefined;

		if (char === '"' && prevChar !== '\\') {
			return { partial, endIndex: i };
		} else {
			partial += char;
		}
	}

	return { partial, endIndex: null };
}

export class PartialStreamParser {
	private toolCalls: ToolCall[] = [];
	private accumulated: string = '';

	reset(): void {
		this.toolCalls = [];
		this.accumulated = '';
	}

	parse(input: string): ParseResult[] {
		this.accumulated += input;
		const results: ParseResult[] = [];

		while (true as boolean) {
			let toolCall = this.toolCalls.at(-1);

			if (!toolCall) {
				toolCall = new ToolCall();
				this.toolCalls.push(toolCall);
			}

			/**
			 * Step 1:
			 * Populate the name/type of the command (e.g., appendNode).
			 */
			if (!toolCall.name) {
				// Start searching from the previous tool call, offset
				// by 1 so we don't find the same one again.
				const previousToolCall = this.toolCalls.at(-2);
				const startIdx = (previousToolCall?.nameKeyIdx ?? -1) + 1;
				const result = findValue(this.accumulated.slice(startIdx), NAME);
				if (result) {
					toolCall.name = result.value;
					toolCall.nameKeyIdx = startIdx + result.keyIndex;
				} else {
					return results;
				}
			}

			const searchStartIdx = toolCall.nameKeyIdx;
			if (searchStartIdx === null) {
				return results;
			}
			// TODO: EDITOR-123 - Can we guarantee that the other keys will come after `name`?
			const searchSpace = this.accumulated.slice(searchStartIdx);

			/**
			 * Step 2:
			 * Populate the local ID if it's required.
			 */
			if (requiresLocalId(toolCall.name) && !toolCall.arguments.localId) {
				const result = findValue(searchSpace, LOCAL_ID);

				if (result) {
					toolCall.arguments.localId = result.value;
				} else {
					return results;
				}
			}

			/**
			 * Step 3:
			 * Populate the additional wrap parameters if this is a wrap action.
			 */
			if (isWrapAction(toolCall.name)) {
				if (!toolCall.arguments.wrapperType) {
					const result = findValue(searchSpace, WRAPPER_TYPE);

					if (result) {
						toolCall.arguments.wrapperType = result.value;
					} else {
						return results;
					}
				}

				if (!toolCall.arguments.wrapperAttrs) {
					const result = findValue(searchSpace, WRAPPER_ATTRS);

					if (result) {
						// eslint-disable-next-line require-unicode-regexp
						toolCall.arguments.wrapperAttrs = JSON.parse(result.value.replace(/\\/g, ''));
					} else {
						return results;
					}
				}
			}

			/**
			 * Step 3.1:
			 * Populate the additional update parameters for update attribute actions
			 */
			if (isUpdateAction(toolCall.name)) {
				if (!toolCall.arguments.attrs) {
					const result = findValue(searchSpace, ATTRIBUTES_TYPE);

					if (result) {
						// eslint-disable-next-line require-unicode-regexp
						toolCall.arguments.attrs = JSON.parse(result.value.replace(/\\/g, ''));
					} else {
						return results;
					}
				}
			}

			/**
			 * Step 4:
			 * At this point, all the information required for initializing
			 * the tool call has been gathered, so push an InitAction and mark
			 * the tool call as initialized.
			 */
			if (!toolCall.initialized) {
				results.push(
					new InitAction(
						toolCall.name as StreamActionType,
						// May be null
						toolCall.arguments.localId,
						// May be null
						toolCall.arguments.wrapperType,
						// May be null
						toolCall.arguments.wrapperAttrs,
						// May be null
						toolCall.arguments.attrs,
					),
				);
				toolCall.initialized = true;
			}

			/**
			 * Step 5:
			 * If this tool call isn't streamable (e.g., removeNode), then
			 * we can finalize it and move on to the next tool call.
			 */
			if (!isStreamable(toolCall.name)) {
				// If this is an action we don't need an ADF value for (e.g., remove), then
				// we're done and can move on to the next tool call.
				results.push(new FinishAction());
				this.toolCalls.push(new ToolCall());
				continue;
			}

			/**
			 * Step 6:
			 * Find the starting index of the ADF value.
			 * Example:
			 * "value": "{\"content..."
			 *           ^
			 *           |
			 *           +-- This is the start of the ADF value
			 */
			if (!toolCall.arguments.valueStartIdx) {
				const adfStartIdx = findAdfStartIdx(searchSpace);

				if (adfStartIdx !== null) {
					toolCall.arguments.valueStartIdx = searchStartIdx + adfStartIdx;
				} else {
					return results;
				}
			}

			/**
			 * Step 7:
			 * Start looking for the end of the ADF value. If partial ADF has been
			 * found, we push a StreamAction with the new content. If the end index
			 * has been found, we push a FinishAction and move to the next tool call.
			 */
			const valueEnd = findAdfEndIdx(
				this.accumulated.slice(toolCall.arguments.valueStartIdx),
				toolCall.arguments.value.length,
			);

			if (valueEnd.partial.length > 0) {
				toolCall.arguments.value += valueEnd.partial;
				results.push(new StreamAction(valueEnd.partial));
			}

			if (valueEnd.endIndex === null) {
				// We haven't reached the end of the ADF yet
				return results;
			} else {
				// Move to the next tool call
				results.push(new FinishAction());
				this.toolCalls.push(new ToolCall());
			}
		}

		return results;
	}
}
